import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import MobileLayout from '@/components/layout/MobileLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import { images } from '@/assets/images';

type ResetStatus = 'idle' | 'loading' | 'success' | 'error';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<ResetStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    try {
      setStatus('loading');
      setErrorMessage(null);

      const redirectUrl = `${window.location.origin}/password-reset`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        throw error;
      }

      setStatus('success');
      toast({
        title: "Reset email sent",
        description: "Check your email for the password reset link.",
      });
    } catch (error) {
      console.error("Error requesting password reset:", error);
      setStatus('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again."
      );
    }
  };

  return (
    <MobileLayout hideNavigation>
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-8 px-6">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src={images.equiforgeLogoFlat}
                alt="EquiForge Logo"
                className="h-16 md:h-20 w-auto object-contain drop-shadow-lg"
              />
            </div>

            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2">Reset Your Password</h1>
              
              {/* Success State */}
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center text-center p-4">
                  <div className="bg-green-100 dark:bg-green-900/20 rounded-full p-3 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-xl font-medium mb-2">Check Your Email</h2>
                  <p className="text-muted-foreground mb-4">
                    We've sent a password reset link to <strong>{email}</strong>.
                    Please check your inbox and spam folder.
                  </p>
                  <div className="space-y-2 w-full">
                    <Button 
                      onClick={() => navigate('/login')} 
                      className="w-full"
                    >
                      Back to Login
                    </Button>
                    <Button 
                      onClick={() => setStatus('idle')} 
                      variant="outline"
                      className="w-full"
                    >
                      Send Another Email
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-muted-foreground mb-6">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                  
                  <form onSubmit={handleResetRequest} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          disabled={status === 'loading'}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    {errorMessage && (
                      <Alert variant="destructive">
                        <AlertDescription>{errorMessage}</AlertDescription>
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={!email || status === 'loading'}
                    >
                      {status === 'loading' ? "Sending..." : "Send Reset Link"}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => navigate('/login')}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Login
                    </Button>
                  </form>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MobileLayout>
  );
};

export default ForgotPassword;
